import axios from "axios";
import _ from "lodash";

import { bugsnagClient } from "~/shared/utils/errorHandling";
import { usStatesList } from "~/shared/widgets/autocompleteUSStates.jsx";

export function originalAddressReadable(addressObj) {
  if (!_.isEmpty(addressObj)) {
    const { address1, address2, city, usState, zip } = addressObj;
    const stateAbbr =
      typeof usState === "string" ? usState : usState?.abbreviation;
    return `${address1}${address2 ? ` ${address2}` : ""}\n${city}, ${stateAbbr} ${zip}`;
  }
}

export function suggestedAddressReadable(addressObj) {
  const {
    street_number,
    route,
    subpremise,
    locality,
    sublocality_level_1,
    administrative_area_level_1,
    administrative_area_level_3,
    postal_code,
    neighborhood,
  } = addressObj;
  const city =
    locality ||
    sublocality_level_1 ||
    administrative_area_level_3 ||
    neighborhood;

  return `${street_number} ${route}${
    subpremise ? ` ${subpremise}` : ""
  }\n${city}, ${administrative_area_level_1} ${postal_code}`.replace("  ", " ");
}

export function normalizeAddressForValidation(address) {
  if (typeof address.usState === "string") {
    const stateObj = usStatesList.find(
      (s) => s.abbreviation === address.usState
    );
    return {
      ...address,
      usState: stateObj || { abbreviation: address.usState, name: address.usState },
    };
  }
  return address;
}

export function getStateAbbreviation(usState) {
  return typeof usState === "string" ? usState : usState?.abbreviation;
}

const cleanAddress = (address) => {
  return address.replace("'", "");
};

export const googleValidateAddress = async (
  apiKey,
  originalAddress,
  userEmail
) => {
  const normalizedAddress = normalizeAddressForValidation(originalAddress);
  const stateAbbr = getStateAbbreviation(normalizedAddress.usState);

  try {
    const res = await axios.post(
      `https://addressvalidation.googleapis.com/v1:validateAddress?key=${apiKey}`,
      {
        address: {
          regionCode: "US",
          locality: normalizedAddress.city,
          administrativeArea: stateAbbr,
          postalCode: normalizedAddress.zip,
          addressLines: cleanAddress(
            `${normalizedAddress.address1}${
              normalizedAddress.address2
                ? ` ${normalizedAddress.address2}`
                : ""
            }`
          ),
        },
        enableUspsCass: true,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const {
      result: { verdict, address, uspsData },
    } = res.data;

    const addressComponentsByType = {};
    address.addressComponents.forEach(({ componentType, componentName }) => {
      addressComponentsByType[componentType] = componentName?.text;
    });

    const hasInferredComponentsOtherThanSuffix = address.addressComponents
      .filter(({ componentType }) => componentType !== "postal_code_suffix")
      .some(({ inferred, spellCorrected }) => inferred || spellCorrected);

    const hasDifferenceWithUSPSState =
      addressComponentsByType.administrative_area_level_1 !==
      uspsData.standardizedAddress.state;

    if (verdict.hasUnconfirmedComponents) {
      const unconfirmedComponents = {};
      address.unconfirmedComponentTypes.forEach((type) => {
        unconfirmedComponents[type] = address.addressComponents?.find(
          ({ componentType }) => componentType === type
        ).componentName?.text;
      });
      return {
        modalName: "couldntVerifyAddress",
        modalData: {
          originalAddress: normalizedAddress,
          unconfirmedComponents,
        },
      };
    } else if (
      verdict.hasReplacedComponents ||
      hasInferredComponentsOtherThanSuffix ||
      hasDifferenceWithUSPSState
    ) {
      const suggestedAddress = {};
      address.addressComponents.forEach(
        ({ componentType, componentName: { text }, spellCorrected }) => {
          let addressText = text;
          if (
            componentType === "administrative_area_level_1" &&
            !spellCorrected
          ) {
            addressText = uspsData.standardizedAddress.state;
          }
          suggestedAddress[componentType] = addressText;
        }
      );

      if (
        originalAddressReadable(normalizedAddress).toLowerCase() ===
        suggestedAddressReadable(suggestedAddress).toLowerCase()
      ) {
        return false;
      }

      return {
        modalName: "confirmAddress",
        modalData: {
          originalAddress: normalizedAddress,
          suggestedAddress,
        },
      };
    } else if (
      address.missingComponentTypes &&
      address.missingComponentTypes.includes("subpremise") &&
      !addressComponentsByType.post_box
    ) {
      return {
        modalName: "missingAddressComponents",
        modalData: {
          originalAddress: normalizedAddress,
        },
      };
    } else {
      return false;
    }
  } catch (e) {
    console.error("[googleValidateAddress] Error: ", e);
    if (bugsnagClient) {
      bugsnagClient.user = {
        email: userEmail || "unknown",
      };
      bugsnagClient.notify(e);
    }
    return false;
  }
};

export function convertSuggestedAddressToFormFormat(suggestedAddress) {
  const stateObj = usStatesList.find(
    ({ abbreviation }) =>
      abbreviation === suggestedAddress.administrative_area_level_1
  );

  return {
    address1: `${suggestedAddress.street_number} ${suggestedAddress.route}`,
    address2: suggestedAddress.subpremise || "",
    city:
      suggestedAddress.locality ||
      suggestedAddress.sublocality_level_1 ||
      suggestedAddress.administrative_area_level_3 ||
      suggestedAddress.neighborhood,
    usState: stateObj,
    zip: suggestedAddress.postal_code,
  };
}

export function convertSuggestedAddressToStringStateFormat(suggestedAddress) {
  return {
    address1: `${suggestedAddress.street_number} ${suggestedAddress.route}`,
    address2: suggestedAddress.subpremise || "",
    city:
      suggestedAddress.locality ||
      suggestedAddress.sublocality_level_1 ||
      suggestedAddress.administrative_area_level_3 ||
      suggestedAddress.neighborhood,
    usState: suggestedAddress.administrative_area_level_1,
    zip: suggestedAddress.postal_code,
  };
}
